import { useState, useEffect, useRef } from "react";
import { Instagram, X, ChevronLeft, ChevronRight } from "lucide-react";

const REVIEWS = [
  {
    handle: "mapusagamasofa13",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/dpuy60sg_4BFB0DB1-6D92-48D8-B115-6697AC840C72.JPG",
  },
  {
    handle: "lucrickk",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/tr652t7a_6A8CD990-C10F-418B-BF23-1C6DAD6402A1.JPG",
  },
  {
    handle: "zhanice_1.0",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/zig82oy0_67ED682A-ACAE-4B47-9D74-3630D43C53AA.JPG",
  },
  {
    handle: "its_baluu_yo",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/a26qf4da_46427C62-4627-4FBA-9CAB-3ACE5F490279.JPG",
  },
  {
    handle: "joannepua_",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/0m2kac8z_24895715-6EBC-411A-9C3A-F8CE8FC335B9.JPG",
  },
  {
    handle: "_luke3976",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/hd3b5nhc_B4980763-F9D7-4E10-8FB8-D44162462512.JPG",
  },
  {
    handle: "rlz.guss",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/30adzd8u_CDD4D926-6BF0-4BE1-AF4A-97D964FCBD1A.JPG",
  },
  {
    handle: "irenenyro_",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/oxcp9ekt_DA7C9EB3-7CBA-40B6-AEF7-657D1A8ACDF6.JPG",
  },
  {
    handle: "jo.ropati",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/dt8evg4i_E8EDEC66-EE74-460F-945E-113F17E449CD.JPG",
  },
  {
    handle: "tleungwo__",
    src: "https://customer-assets.emergentagent.com/job_nibs-southeast/artifacts/024v13pm_F8D66E1F-02EE-4594-95A5-ABF7163EE026.JPG",
  },
];

export default function SocialProof() {
  const [activeIndex, setActiveIndex] = useState(null);
  const active = activeIndex !== null ? REVIEWS[activeIndex] : null;
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const close = () => setActiveIndex(null);
  const prev = () => setActiveIndex((i) => (i === null ? null : (i - 1 + REVIEWS.length) % REVIEWS.length));
  const next = () => setActiveIndex((i) => (i === null ? null : (i + 1) % REVIEWS.length));

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [active]);

  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next();
      else prev();
    }
  };

  return (
    <section
      id="gallery"
      data-testid="social-proof-section"
      className="relative py-20 md:py-28 bg-[#0A0D26] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#F4A623]">The Hype</div>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-[#F6F0E8]">
              Burn city <span className="text-[#ED0B09] text-glow-red">approved</span>
            </h2>
          </div>
          <a
            href="https://instagram.com/gullwaynibs"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-[#F4A623] hover:text-[#F6F0E8] transition-colors"
          >
            <Instagram size={16} /> Tag us @gullwaynibs
          </a>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {REVIEWS.map((r, i) => (
            <button
              type="button"
              key={r.handle}
              onClick={() => setActiveIndex(i)}
              data-testid={`review-tile-${i}`}
              className="group relative aspect-[9/16] overflow-hidden rounded-2xl border border-[#F6F0E8]/10 hover:border-[#F4A623]/40 transition-all"
              aria-label={`Open review from ${r.handle}`}
            >
              <img
                src={r.src}
                alt={`Review from @${r.handle}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D26]/90 via-transparent to-[#0A0D26]/30 opacity-90" />
              <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 bg-[#0A0D26]/70 backdrop-blur-md border border-[#F6F0E8]/15 text-[#F6F0E8] text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">
                <Instagram size={10} className="text-[#F4A623]" />
                <span className="truncate max-w-[120px]">@{r.handle}</span>
              </div>
              <div className="absolute bottom-2 left-2 right-2 text-[10px] uppercase tracking-widest text-[#F4A623]">
                IG Story
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-[#0A0D26]/95 backdrop-blur-md flex flex-col"
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="review-lightbox"
          role="dialog"
          aria-modal="true"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 shrink-0" onClick={(e) => e.stopPropagation()}>
            <div className="inline-flex items-center gap-1.5 bg-[#171D4F]/70 backdrop-blur-md border border-[#F6F0E8]/15 text-[#F6F0E8] text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded-full">
              <Instagram size={11} className="text-[#F4A623]" /> @{active.handle}
            </div>
            <div className="flex items-center gap-3">
              <span
                data-testid="review-lightbox-counter"
                className="text-xs uppercase tracking-widest text-[#F6F0E8]/65"
              >
                {activeIndex + 1} / {REVIEWS.length}
              </span>
              <button
                type="button"
                onClick={close}
                data-testid="review-lightbox-close"
                aria-label="Close"
                className="h-10 w-10 rounded-full bg-[#171D4F]/80 hover:bg-[#ED0B09] border border-[#F6F0E8]/15 text-[#F6F0E8] flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Image area */}
          <div className="flex-1 min-h-0 flex items-center justify-center px-4 sm:px-6 pb-6 relative">
            {/* Prev / Next (desktop) */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="review-lightbox-prev"
              aria-label="Previous"
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-[#171D4F]/70 hover:bg-[#ED0B09] border border-[#F6F0E8]/15 text-[#F6F0E8] items-center justify-center transition-colors"
            >
              <ChevronLeft size={22} />
            </button>
            <img
              key={active.src}
              src={active.src}
              alt={`Review from @${active.handle}`}
              onClick={(e) => e.stopPropagation()}
              draggable={false}
              className="max-h-full max-w-full w-auto h-auto rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] select-none"
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="review-lightbox-next"
              aria-label="Next"
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-[#171D4F]/70 hover:bg-[#ED0B09] border border-[#F6F0E8]/15 text-[#F6F0E8] items-center justify-center transition-colors"
            >
              <ChevronRight size={22} />
            </button>
          </div>

          {/* Mobile swipe hint + dots */}
          <div className="sm:hidden flex items-center justify-center gap-1.5 pb-6 shrink-0" onClick={(e) => e.stopPropagation()}>
            {REVIEWS.map((_, i) => (
              <button
                type="button"
                key={i}
                onClick={() => setActiveIndex(i)}
                aria-label={`Go to review ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === activeIndex ? "w-5 bg-[#F4A623]" : "w-1.5 bg-[#F6F0E8]/30"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
